import { ChangeDetectorRef, Component, DestroyRef, NgZone, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { ApiLoadErrorComponent } from '../../components/api-load-error/api-load-error.component';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';
import { SubscriptionPlan } from '../../models/platform-access.models';
import { AdminApiError, AdminAssistantCreditPack, AdminService } from '../../services/admin.service';
import { ForegroundRefreshService } from '../../services/foreground-refresh.service';

interface PlanDraft {
  name: string;
  description: string;
  priceEur: number;
  sortOrder: number;
  maxActiveWebsites: number | null;
  maxTeamMembers: number | null;
  allowCustomDomain: boolean;
  allowExport: boolean;
  allowPublish: boolean;
  templateAccessLevel: string;
  supportLevel: string;
  selfServeVisible: boolean;
  paddleBillingEnabled: boolean;
  assistantIncludedTokens: number;
}

interface CreditPackDraft {
  label: string;
  description: string;
  tokenAmount: number;
  priceEur: number;
  isActive: boolean;
  sortOrder: number;
}

const RECOMMENDED_AI_CREDIT_PACKS: Array<Omit<CreditPackDraft, 'isActive' | 'sortOrder'>> = [
  {
    label: '100,000 AI tokens',
    description: 'A small prepaid BizzWeaver AI pack for focused website changes.',
    tokenAmount: 100_000,
    priceEur: 3,
  },
  {
    label: '500,000 AI tokens',
    description: 'A prepaid BizzWeaver AI pack for iterative website design and building.',
    tokenAmount: 500_000,
    priceEur: 13,
  },
  {
    label: '1,000,000 AI tokens',
    description: 'The best-value prepaid BizzWeaver AI pack for substantial website work.',
    tokenAmount: 1_000_000,
    priceEur: 24,
  },
];

@Component({
  selector: 'app-admin-plans-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ApiLoadErrorComponent, InfoTooltipComponent],
  templateUrl: './admin-plans-page.component.html',
  styleUrls: ['./admin-plans-page.component.scss']
})
export class AdminPlansPageComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  isInitialLoading = true;
  hasInitialLoadError = false;
  isSaving = false;
  saveMessage = '';
  saveError = '';
  selectedPlanCode = '';
  plans: SubscriptionPlan[] = [];
  draft: PlanDraft | null = null;
  creditPacks: AdminAssistantCreditPack[] = [];
  selectedCreditPackId: string | null = null;
  creditPackDraft: CreditPackDraft = this.createNewCreditPackDraft();
  isCreditPacksLoading = false;
  isCreditPackSaving = false;
  isCreatingRecommendedCreditPacks = false;
  creditPackMessage = '';
  creditPackError = '';
  private isBackgroundRefreshInFlight = false;

  constructor(
    private readonly adminService: AdminService,
    private readonly foregroundRefresh: ForegroundRefreshService
  ) {}

  ngOnInit(): void {
    this.loadPlans();
    this.loadCreditPacks();
    this.foregroundRefresh.watch(5000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.refreshInBackground());
  }

  get selectedPlan(): SubscriptionPlan | null {
    return this.plans.find((plan) => plan.code === this.selectedPlanCode) ?? null;
  }

  get selectedCreditPack(): AdminAssistantCreditPack | null {
    return this.creditPacks.find((pack) => pack.publicId === this.selectedCreditPackId) ?? null;
  }

  get selectedPlanPriceLabel(): string {
    const plan = this.selectedPlan;
    if (!plan) {
      return '';
    }

    if (plan.priceEur === 0) {
      return 'Free';
    }

    return `${plan.priceEur} EUR / ${plan.billingInterval}`;
  }

  loadPlans(preserveSelection?: string): void {
    this.isInitialLoading = true;
    this.hasInitialLoadError = false;
    this.saveError = '';

    this.adminService.getPlans()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.runInAngular(() => {
            this.plans = state.plans;
            const requestedSelection = preserveSelection || this.selectedPlanCode;
            const nextSelection = state.plans.some((plan) => plan.code === requestedSelection)
              ? requestedSelection
              : (state.plans[0]?.code || '');
            this.selectPlan(nextSelection);
            this.isInitialLoading = false;
          });
        },
        error: () => {
          this.runInAngular(() => {
            this.hasInitialLoadError = true;
            this.isInitialLoading = false;
          });
        }
      });
  }

  selectPlan(planCode: string): void {
    this.selectedPlanCode = planCode;
    const plan = this.plans.find((entry) => entry.code === planCode) ?? null;
    this.draft = plan ? this.createDraft(plan) : null;
    this.saveError = '';
    this.saveMessage = '';
  }

  resetDraft(): void {
    if (this.selectedPlan) {
      this.draft = this.createDraft(this.selectedPlan);
      this.saveError = '';
    }
  }

  savePlan(): void {
    if (!this.selectedPlan || !this.draft) {
      return;
    }

    this.saveError = '';
    this.saveMessage = '';
    this.isSaving = true;

    this.adminService.updatePlan(this.selectedPlan.code, {
      name: this.draft.name.trim(),
      description: this.normalizeOptionalText(this.draft.description),
      priceEur: this.draft.priceEur,
      sortOrder: this.draft.sortOrder,
      maxActiveWebsites: this.normalizeOptionalInt(this.draft.maxActiveWebsites),
      maxTeamMembers: this.normalizeOptionalInt(this.draft.maxTeamMembers),
      allowCustomDomain: this.draft.allowCustomDomain,
      allowExport: this.draft.allowExport,
      allowPublish: this.draft.allowPublish,
      templateAccessLevel: this.draft.templateAccessLevel.trim(),
      supportLevel: this.draft.supportLevel.trim(),
      selfServeVisible: this.draft.selfServeVisible,
      paddleBillingEnabled: this.selectedPlan.code === 'free' ? false : this.draft.paddleBillingEnabled,
      assistantIncludedTokens: this.draft.assistantIncludedTokens,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.runInAngular(() => {
            this.saveMessage = response.message;
            this.isSaving = false;
            this.loadPlans(this.selectedPlanCode);
          });
        },
        error: (error: AdminApiError) => {
          this.runInAngular(() => {
            this.saveError = error.message;
            this.isSaving = false;
          });
        }
      });
  }

  loadCreditPacks(selectCreditPackId?: string | null): void {
    this.isCreditPacksLoading = true;
    this.creditPackError = '';
    this.adminService.getAssistantCreditPacks()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (packs) => {
          this.runInAngular(() => {
            this.creditPacks = packs;
            const nextId = selectCreditPackId ?? this.selectedCreditPackId;
            if (nextId && packs.some((pack) => pack.publicId === nextId)) {
              this.selectCreditPack(nextId);
            } else {
              this.newCreditPack();
            }
            this.isCreditPacksLoading = false;
          });
        },
        error: (error: AdminApiError) => {
          this.runInAngular(() => {
            this.creditPackError = error.message;
            this.isCreditPacksLoading = false;
          });
        }
      });
  }

  private refreshInBackground(): void {
    if (this.isBackgroundRefreshInFlight || this.isInitialLoading || this.isSaving || this.isCreditPacksLoading || this.isCreditPackSaving || this.isCreatingRecommendedCreditPacks) return;
    this.isBackgroundRefreshInFlight = true;
    Promise.all([
      firstValueFrom(this.adminService.getPlans()),
      firstValueFrom(this.adminService.getAssistantCreditPacks())
    ]).then(([planState, packs]) => this.runInAngular(() => {
      this.plans = planState.plans;
      if (this.selectedPlanCode && !this.plans.some((plan) => plan.code === this.selectedPlanCode)) {
        this.selectedPlanCode = this.plans[0]?.code ?? '';
      }
      this.creditPacks = packs;
      this.isBackgroundRefreshInFlight = false;
    })).catch(() => this.runInAngular(() => this.isBackgroundRefreshInFlight = false));
  }

  selectCreditPack(creditPackId: string): void {
    const pack = this.creditPacks.find((entry) => entry.publicId === creditPackId);
    if (!pack) return;
    this.selectedCreditPackId = pack.publicId;
    this.creditPackDraft = this.createCreditPackDraft(pack);
    this.creditPackError = '';
    this.creditPackMessage = '';
  }

  newCreditPack(): void {
    this.selectedCreditPackId = null;
    this.creditPackDraft = this.createNewCreditPackDraft();
    this.creditPackError = '';
    this.creditPackMessage = '';
  }

  async createRecommendedCreditPacks(): Promise<void> {
    if (this.isCreditPackSaving || this.isCreatingRecommendedCreditPacks) return;
    const existingAmounts = new Set(this.creditPacks.map((pack) => pack.tokenAmount));
    const packsToCreate = RECOMMENDED_AI_CREDIT_PACKS.filter((pack) => !existingAmounts.has(pack.tokenAmount));
    if (packsToCreate.length === 0) {
      this.creditPackMessage = 'The recommended AI token packs already exist. Review and activate them when ready.';
      return;
    }

    this.creditPackError = '';
    this.creditPackMessage = '';
    this.isCreatingRecommendedCreditPacks = true;
    try {
      let createdPackId: string | null = null;
      for (const [index, pack] of packsToCreate.entries()) {
        const created = await firstValueFrom(this.adminService.createAssistantCreditPack({
          label: pack.label,
          description: pack.description,
          tokenAmount: pack.tokenAmount,
          priceCents: Math.round(pack.priceEur * 100),
          currency: 'EUR',
          isActive: false,
          sortOrder: this.creditPacks.length + index,
        }));
        createdPackId = created.publicId;
      }
      this.creditPackMessage = 'Recommended AI token packs were created as inactive drafts. Review the prices, then activate the packs you want to offer.';
      this.loadCreditPacks(createdPackId);
    } catch (error) {
      this.creditPackError = (error as AdminApiError).message || 'The recommended AI token packs could not be created.';
    } finally {
      this.runInAngular(() => {
        this.isCreatingRecommendedCreditPacks = false;
      });
    }
  }

  saveCreditPack(): void {
    const draft = this.creditPackDraft;
    this.creditPackError = '';
    this.creditPackMessage = '';
    this.isCreditPackSaving = true;
    const payload = {
      label: draft.label.trim(),
      description: this.normalizeOptionalText(draft.description),
      tokenAmount: Math.round(draft.tokenAmount),
      priceCents: Math.round(draft.priceEur * 100),
      currency: 'EUR' as const,
      isActive: draft.isActive,
      sortOrder: Math.max(0, Math.round(draft.sortOrder))
    };
    const request = this.selectedCreditPackId
      ? this.adminService.updateAssistantCreditPack(this.selectedCreditPackId, payload)
      : this.adminService.createAssistantCreditPack(payload);
    request
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (pack) => {
          this.runInAngular(() => {
            this.creditPackMessage = this.selectedCreditPackId
              ? 'AI credit pack updated. A changed price is synchronized as a new Paddle price when it is next offered.'
              : 'AI credit pack created. Activate it when its price and token quantity are ready for customers.';
            this.isCreditPackSaving = false;
            this.loadCreditPacks(pack.publicId);
          });
        },
        error: (error: AdminApiError) => {
          this.runInAngular(() => {
            this.creditPackError = error.message;
            this.isCreditPackSaving = false;
          });
        }
      });
  }

  private createDraft(plan: SubscriptionPlan): PlanDraft {
    return {
      name: plan.name,
      description: plan.description ?? '',
      priceEur: plan.priceEur,
      sortOrder: plan.sortOrder,
      maxActiveWebsites: plan.entitlements.maxActiveWebsites,
      maxTeamMembers: plan.entitlements.maxTeamMembers,
      allowCustomDomain: plan.entitlements.allowCustomDomain,
      allowExport: plan.entitlements.allowExport,
      allowPublish: plan.entitlements.allowPublish,
      templateAccessLevel: plan.entitlements.templateAccessLevel,
      supportLevel: plan.entitlements.supportLevel,
      selfServeVisible: plan.selfServeVisible,
      paddleBillingEnabled: plan.paddleBillingEnabled,
      assistantIncludedTokens: plan.assistantIncludedTokens,
    };
  }

  private createNewCreditPackDraft(): CreditPackDraft {
    return {
      label: '',
      description: '',
      tokenAmount: 100000,
      priceEur: 2,
      isActive: false,
      sortOrder: this.creditPacks.length
    };
  }

  private createCreditPackDraft(pack: AdminAssistantCreditPack): CreditPackDraft {
    return {
      label: pack.label,
      description: pack.description ?? '',
      tokenAmount: pack.tokenAmount,
      priceEur: pack.priceCents / 100,
      isActive: pack.isActive,
      sortOrder: pack.sortOrder
    };
  }

  private normalizeOptionalText(value: string): string | null {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }

  private normalizeOptionalInt(value: number | null): number | null {
    if (value === null || Number.isNaN(value)) {
      return null;
    }

    return value;
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }
}
